import React, { useState, useEffect } from "react";
import { ToolCall } from "../../../store/types";
import "./toolCallSection.scss";

interface ToolCallSectionProps {
  toolCall: ToolCall;
}

const ToolCallSection: React.FC<ToolCallSectionProps> = ({ toolCall }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dots, setDots] = useState("");

  useEffect(() => {
    if (toolCall.status === "running") {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
      }, 500);
      return () => clearInterval(interval);
    } else {
      setDots("");
    }
  }, [toolCall.status]);

  const getStatusIcon = () => {
    switch (toolCall.status) {
      case "running":
        return <div className="tool-status-icon running">⏳</div>;
      case "completed":
        return <div className="tool-status-icon completed">✅</div>;
      case "error":
        return <div className="tool-status-icon error">❌</div>;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (toolCall.status) {
      case "running":
        return (
          <span className="running-text">
            Running<span className="running-dots">{dots}</span>
          </span>
        );
      case "completed":
        return "Completed";
      case "error":
        return "Error";
      default:
        return "Unknown";
    }
  };

  const formatToolName = (name: string) => {
    const formatText = (text: string) => {
      return text
        .replace(/_/g, " ")
        .replace(/-/g, " ")
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
    };

    // Check if name contains double underscore for MCP pattern
    if (name.includes("__")) {
      const [server, tool] = name.split("__");
      return `${formatText(server)} : ${formatText(tool)}`;
    }

    // Fallback: Convert snake_case/kebab-case to Title Case for non-MCP names
    return formatText(name);
  };

  const formatJsonForDisplay = (data: any) => {
    if (typeof data === "string") {
      try {
        return JSON.stringify(JSON.parse(data), null, 2);
      } catch {
        return data;
      }
    }
    return JSON.stringify(data, null, 2);
  };

  return (
    <div className={`tool-call-section ${toolCall.status}`}>
      <div
        className="tool-call-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="tool-call-info">
          {getStatusIcon()}
          <span className="tool-name">
            {formatToolName(toolCall.tool_name)}
          </span>
          <span className="tool-status">{getStatusText()}</span>
        </div>
        <div className={`expand-arrow ${isExpanded ? "expanded" : ""}`}>▼</div>
      </div>

      {isExpanded && (
        <div className="tool-call-details">
          {toolCall.tool_input && (
            <div className="tool-section">
              <h4>Input:</h4>
              <pre className="tool-data">
                {formatJsonForDisplay(toolCall.tool_input)}
              </pre>
            </div>
          )}

          {toolCall.status === "error" && toolCall.tool_result?.error && (
            <div className="tool-section error-section">
              <h4>Error:</h4>
              <div className="error-message">{toolCall.tool_result.error}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ToolCallSection;
